/**
 * Utility functions for Google Analytics (GA4)
 * Implementação avançada com suporte para ecommerce, engajamento, funis e métricas customizadas
 */

// Declare global gtag function
declare global {
  interface Window {
    gtag: (
      command: string,
      action: string,
      params?: any
    ) => void;
    dataLayer: any[];
  }
}

/**
 * Tipos de usuário para segmentação
 */
export enum UserType {
  VISITOR = 'visitor',
  REGISTERED = 'registered',
  SUBSCRIBER = 'subscriber',
  CREATOR = 'creator'
}

/**
 * Status de assinatura para segmentação
 */
export enum SubscriptionLevel {
  NONE = 'none',
  ACTIVE = 'active',
  CANCELED = 'canceled',
  EXPIRED = 'expired'
}

/**
 * Track a page view event in Google Analytics
 * 
 * @param page Page path or name
 * @param title Page title (optional)
 */
export const trackPageView = (page: string, title?: string) => {
  if (!window.gtag) return;

  const pageData: any = {
    page_path: page,
  };

  if (title) {
    pageData.page_title = title;
  }

  window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || '', pageData);
};

/**
 * Track a user interaction event in Google Analytics
 * 
 * @param category Event category (e.g., 'User', 'Post', 'Subscription')
 * @param action Event action (e.g., 'Login', 'Register', 'Like')
 * @param label Event label for additional context (optional)
 * @param value Numerical value associated with the event (optional)
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  if (!window.gtag) return;

  const eventParams: any = {
    event_category: category,
    event_label: label,
    value: value,
  };

  window.gtag('event', action, eventParams);
};

/**
 * Track user authentication events
 * 
 * @param method Authentication method used
 * @param successful Whether the authentication was successful
 */
export const trackAuthentication = (method: 'login' | 'register' | 'forgot_password', successful: boolean) => {
  trackEvent(
    'Authentication',
    method,
    successful ? 'Success' : 'Failed'
  );
};

/**
 * Set user ID for Google Analytics
 * Used for cross-device tracking and user behavior analysis
 * 
 * @param userId User identifier
 * @param userType Tipo de usuário (visitor, registered, subscriber, creator)
 * @param subscriptionStatus Status da assinatura do usuário (optional)
 * @param additionalProperties Propriedades adicionais para segmentação (optional)
 */
export const setUserId = (
  userId: string, 
  userType: UserType = UserType.REGISTERED, 
  subscriptionStatus?: SubscriptionLevel,
  additionalProperties?: Record<string, any>
) => {
  if (!window.gtag) return;

  // Configura o user_id para analytics cross-device
  window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || '', {
    user_id: userId
  });

  // Define propriedades customizadas do usuário para segmentação
  const userProperties: Record<string, any> = {
    user_type: userType,
    user_id: userId
  };

  // Adiciona status de assinatura se fornecido
  if (subscriptionStatus) {
    userProperties.subscription_status = subscriptionStatus;
  }

  // Adiciona propriedades adicionais se fornecidas
  if (additionalProperties) {
    Object.assign(userProperties, additionalProperties);
  }

  // Configura propriedades customizadas para o usuário
  window.gtag('set', 'user_properties', userProperties);
};

/**
 * Track post-related events
 * 
 * @param action Action performed on the post
 * @param postId Post identifier
 * @param isFree Whether the post is free or paid
 */
export const trackPostEvent = (
  action: 'create' | 'view' | 'like' | 'unlike' | 'delete',
  postId: string,
  isFree?: boolean
) => {
  trackEvent(
    'Post',
    action,
    postId,
    isFree === undefined ? undefined : (isFree ? 1 : 0)
  );
};

/**
 * Track subscription-related events
 * Versão básica para compatibilidade
 * 
 * @param action Action performed
 * @param creatorId Creator identifier
 * @param planName Plan name or identifier
 */
export const trackSubscriptionEvent = (
  action: 'subscribe' | 'unsubscribe' | 'view',
  creatorId: string,
  planName?: string
) => {
  trackEvent(
    'Subscription',
    action,
    planName || creatorId
  );
};

/**
 * Track ecommerce subscription purchase
 * Rastreamento avançado de compras usando o modelo de ecommerce do GA4
 * 
 * @param subscriptionId ID único da transação de assinatura
 * @param creatorId ID do creator
 * @param planId ID do plano adquirido
 * @param planName Nome do plano (opcional)
 * @param value Valor da assinatura em centavos
 * @param currency Moeda da transação (padrão: BRL)
 */
export const trackSubscriptionPurchase = (
  subscriptionId: string,
  creatorId: string,
  planId: string,
  planName: string,
  value: number,
  currency: string = 'BRL'
) => {
  if (!window.gtag) return;

  // Converte o valor de centavos para real
  const valueInCurrency = value / 100;
  
  // Rastreia o evento de compra no formato de ecommerce do GA4
  window.gtag('event', 'purchase', {
    transaction_id: subscriptionId,
    value: valueInCurrency,
    currency: currency,
    tax: 0,
    shipping: 0,
    items: [
      {
        item_id: planId,
        item_name: planName || `Assinatura de ${creatorId}`,
        item_category: 'Subscription',
        item_category2: 'Creator Content',
        item_variant: creatorId,
        price: valueInCurrency,
        quantity: 1
      }
    ]
  });
};

/**
 * Track subscription cancellation
 * Rastreamento avançado de cancelamentos usando o modelo de ecommerce do GA4
 * 
 * @param subscriptionId ID único da assinatura cancelada
 * @param creatorId ID do creator
 * @param planId ID do plano cancelado
 * @param planName Nome do plano (opcional)
 * @param daysActive Número de dias que a assinatura esteve ativa
 * @param reason Motivo do cancelamento (opcional)
 */
export const trackSubscriptionCancellation = (
  subscriptionId: string,
  creatorId: string,
  planId?: string,
  planName?: string,
  daysActive?: number,
  reason?: string
) => {
  if (!window.gtag) return;
  
  // Rastreia o evento de cancelamento como um evento customizado
  window.gtag('event', 'subscription_cancel', {
    transaction_id: subscriptionId,
    creator_id: creatorId,
    // plan_id: planId,
    // plan_name: planName || `Assinatura de ${creatorId}`,
    // active_days: daysActive,
    // reason: reason || 'not_specified'
  });
};

/**
 * Track checkout steps in subscription flow
 * Rastreia o funil de assinatura
 * 
 * @param step Etapa do processo de checkout (1-5)
 * @param creatorId ID do creator
 * @param planId ID do plano
 * @param option Opção selecionada na etapa (opcional)
 */
export const trackCheckoutStep = (
  step: number,
  creatorId: string,
  planId: string,
  option?: string
) => {
  if (!window.gtag) return;

  const stepLabels = [
    'view_subscription',
    'begin_checkout',
    'add_payment_info', 
    'process_payment',
    'purchase_complete'
  ];
  
  // Garante que step é um número válido (1-5)
  const stepIndex = Math.max(1, Math.min(5, step)) - 1;
  
  window.gtag('event', stepLabels[stepIndex], {
    items: [
      {
        item_id: planId,
        item_name: `Assinatura de ${creatorId}`,
        item_category: 'Subscription',
        creator_id: creatorId,
        quantity: 1
      }
    ],
    checkout_step: step,
    checkout_option: option
  });
};

/**
 * Track creator account events
 * 
 * @param action Action performed
 * @param status Current status of creator account
 */
export const trackCreatorEvent = (
  action: 'apply' | 'update' | 'settings_change',
  status?: string
) => {
  trackEvent(
    'Creator',
    action,
    status
  );
}

/**
 * Track follow/unfollow events
 * 
 * @param action Action performed (follow or unfollow)
 * @param creatorId Creator identifier
 */
export const trackFollowEvent = (
  action: 'follow' | 'unfollow',
  creatorId: string
) => {
  trackEvent(
    'Social',
    action,
    creatorId
  );
};

/**
 * Track user engagement with content
 * Rastreia o engajamento detalhado dos usuários com o conteúdo
 * 
 * @param contentId ID do conteúdo sendo consumido (post, vídeo, etc)
 * @param contentType Tipo de conteúdo (post, image, video)
 * @param engagementType Tipo de engajamento (view, scroll, play, pause, complete)
 * @param engagementValue Valor numérico do engajamento (tempo, porcentagem, etc)
 * @param creatorId ID do creator do conteúdo (opcional)
 */
export const trackContentEngagement = (
  contentId: string,
  contentType: 'post' | 'image' | 'video',
  engagementType: 'view' | 'scroll' | 'play' | 'pause' | 'complete',
  engagementValue: number,
  creatorId?: string
) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'content_engagement', {
    content_id: contentId,
    content_type: contentType,
    engagement_type: engagementType,
    engagement_value: engagementValue,
    creator_id: creatorId
  });
};

/**
 * Track time spent viewing content
 * Registra o tempo que um usuário gasta visualizando conteúdo específico
 * 
 * @param contentId ID do conteúdo (post, perfil, etc)
 * @param contentType Tipo de conteúdo (post, profile, feed)
 * @param timeSpentSeconds Tempo gasto em segundos
 * @param creatorId ID do creator associado (opcional)
 */
export const trackTimeSpent = (
  contentId: string,
  contentType: 'post' | 'profile' | 'feed',
  timeSpentSeconds: number,
  creatorId?: string
) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'time_spent', {
    content_id: contentId,
    content_type: contentType,
    seconds: timeSpentSeconds,
    creator_id: creatorId
  });
};

/**
 * Track navigation pattern
 * Rastreia padrões de navegação entre diferentes seções do app
 * 
 * @param fromSection Seção de origem
 * @param toSection Seção de destino
 * @param method Método de navegação (click, swipe, auto)
 */
export const trackNavigation = (
  fromSection: string,
  toSection: string,
  method: 'click' | 'swipe' | 'auto' = 'click'
) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'app_navigation', {
    from_section: fromSection,
    to_section: toSection,
    navigation_method: method
  });
};

/**
 * Track errors and exceptions
 * Rastreia erros e exceções para identificar problemas na aplicação
 * 
 * @param errorType Tipo de erro (api, validation, client, server)
 * @param errorMessage Mensagem de erro
 * @param isFatal Se o erro é fatal (impede a operação por completo)
 * @param componentName Nome do componente onde ocorreu o erro (opcional)
 */
export const trackError = (
  errorType: 'api' | 'validation' | 'client' | 'server',
  errorMessage: string,
  isFatal: boolean = false,
  componentName?: string
) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'exception', {
    description: errorMessage,
    fatal: isFatal,
    error_type: errorType,
    component: componentName
  });
};

/**
 * Track funnel steps
 * Rastreia etapas em funis de conversão customizados
 * 
 * @param funnelName Nome do funil (auth, content_creation, subscription)
 * @param stepNumber Número da etapa
 * @param stepName Nome da etapa
 * @param additionalData Dados adicionais relacionados à etapa
 */
export const trackFunnelStep = (
  funnelName: 'auth' | 'content_creation' | 'subscription',
  stepNumber: number,
  stepName: string,
  additionalData?: Record<string, any>
) => {
  if (!window.gtag) return;
  
  const eventParams: Record<string, any> = {
    funnel_name: funnelName,
    step_number: stepNumber,
    step_name: stepName
  };
  
  // Adiciona dados adicionais se fornecidos
  if (additionalData) {
    Object.assign(eventParams, additionalData);
  }
  
  window.gtag('event', `${funnelName}_funnel_step`, eventParams);
};

/**
 * Track feature usage
 * Rastreia o uso de funcionalidades específicas da plataforma
 * 
 * @param featureName Nome da funcionalidade
 * @param action Ação realizada (view, interact, complete)
 * @param value Valor numérico associado (opcional)
 */
export const trackFeatureUsage = (
  featureName: string,
  action: 'view' | 'interact' | 'complete',
  value?: number
) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'feature_usage', {
    feature_name: featureName,
    action: action,
    value: value
  });
};